/**
 * ResponsePane.tsx
 *
 * The component that incapsulates the Transaction workflow
 */

import React, { Fragment, useEffect, useReducer, useState } from "react"

import { CommonText, FieldName } from "../common/text"
import Button from "./generic/buttons/Button"
import { LoadingPanel } from "./generic"
import { endTransaction } from "../containers/transactionSlice"
import TransactionItem from "./transaction/TransactionItem"
import { AirportSeasonConfigurations } from "../api/AirportSeasonConfiguration"
import { Transaction, Transactions } from "../api/Transaction"
import { Flights } from "../api/Flight"
import _ from "lodash"
import {
  EditMode,
  FlightMode,
  GCRMode,
  handleHasFlightsWithParkingLoc,
  handleHasFlightsWithServiceTypeAcReg,
  handleHasFlightsWithTerminal
} from "../common/transaction"
import { handleGetTransactionPageGroup, PageId } from "../common/pageId"
import SeasonSelector from "./SeasonSelector"
import { ResponsePaneText } from "../common/text"
import Stepper from "./generic/stepper/Stepper"
import TextArea from "./generic/form/TextArea"
import TransactionFlightHeaders from "./transaction/TransactionFlightHeaders"
import { CodeType } from "./AirportSelector"
import {
  handleMakeAddAndSendTransactions,
  handleSaveAndSendTransactions
} from "../pages/Support"
import { IErrorObject } from "../api/Communication"
import { PageTitle } from "../common/pageText"
import AlertBox from "./alertBox"
import {
  closeAlmostAllNotifications,
  NotificationKind
} from "../containers/notificationSlice"
import { PageTemplate } from "./pageTemplate/PageTemplate"
import store from "../app/Store"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import { getPageConfig } from "../pages/Config"

export interface ITransactionPopup {
  title: string
  pageId: PageId
  season: string
  useLocalTime: boolean
  transactions: Transactions
  codeType: CodeType
  airportSeasons: AirportSeasonConfigurations
  handleAsBlock: boolean
  siText: string
  onClose: () => void
  onCancel: () => void
  fromEditMode: EditMode
}

export type ActionType = "SET_WORKFLOW_STATE"

export type WorkflowState =
  | "IDLE"
  | "INITIAL_SUBMIT"
  | "FINAL_SUBMIT"
  | "INITIAL_RESPONSE"
  | "FINAL_RESPONSE"
  | "INITIAL_ERROR"
  | "FINAL_ERROR"

export interface ITransactionState {
  workFlowState: WorkflowState
}

enum Step {
  InitialSubmit = 1,
  InitialResponse = 2,
  FinalSubmit = 3,
  FinalResponse = 4
}

export const transactionReducer = (
  state: ITransactionState,
  action: { type: ActionType; payload?: any }
) => {
  switch (action.type) {
    case "SET_WORKFLOW_STATE":
      return {
        ...state,
        workFlowState: action.payload
      }
    default:
      return state
  }
}

export const ResponsePane: React.FunctionComponent<ITransactionPopup> = (
  props
) => {
  const [isLoading, setIsLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(Step.InitialSubmit)
  const [error, setError] = useState<IErrorObject>(null)
  const [infoMsg, setInfoMsg] = useState<string>(null)
  const globalDispatch = useAppDispatch()
  const inTransaction = useAppSelector(
    (state) => state["transactionReducer"].inTransaction
  )
  const [responseTransactions, setResponseTransactions] =
    useState<Transactions>(null)

  const [state, dispatch] = useReducer(transactionReducer, {
    workFlowState: "IDLE"
  })

  const [siText, setSiText] = useState("")

  const transactionPageGroup = handleGetTransactionPageGroup(props.pageId)
  const pageConfig = getPageConfig[props.pageId]

  const onlyFinalResponse = props.pageId === PageId.EditOffers

  useEffect(() => {
    if (inTransaction) {
      if (onlyFinalResponse) {
        startFinalResponseFlow()
      } else {
        startResponseFlow()
      }
      setSiText("")
    }
    // eslint-disable-next-line
  }, [inTransaction])

  const handleResponseSuccess = (
    transactions: Transactions,
    isFinal: boolean,
    infoMsg?: string
  ) => {
    setResponseTransactions(transactions)
    setCurrentStep(
      isFinal && !onlyFinalResponse ? Step.FinalResponse : Step.InitialResponse
    )
    setIsLoading(false)
    dispatch({
      type: "SET_WORKFLOW_STATE",
      payload: isFinal ? "FINAL_RESPONSE" : "INITIAL_RESPONSE"
    })
    if (infoMsg) {
      setInfoMsg(infoMsg)
    }
  }

  const handleResponseError = (error: IErrorObject, isFinal: boolean) => {
    setIsLoading(false)
    setCurrentStep(Step.InitialResponse)
    dispatch({
      type: "SET_WORKFLOW_STATE",
      payload: isFinal ? "INITIAL_RESPONSE" : "INITIAL_ERROR"
    })
    if (error && error.message) {
      setError(error)
    }
  }

  const handleSendTransactions = (isFinal: boolean) => {
    setIsLoading(true)
    handleSaveAndSendTransactions(
      props.pageId,
      isFinal,
      responseTransactions ?? props.transactions,
      props.season,
      props.useLocalTime,
      props.handleAsBlock,
      isFinal ? siText : "",
      (transactions: Transactions, infoMsg?: string) =>
        handleResponseSuccess(transactions, isFinal, infoMsg),
      (error: IErrorObject) => handleResponseError(error, isFinal)
    )
  }

  const handleAddTransactions = (flights: Flights) => {
    const isFinal = false
    setIsLoading(true)
    handleMakeAddAndSendTransactions(
      props.pageId,
      isFinal,
      flights,
      props.season,
      props.useLocalTime,
      props.handleAsBlock,
      isFinal ? siText : "",
      (transactions: Transactions, infoMsg?: string) =>
        handleResponseSuccess(transactions, false, infoMsg),
      (error: IErrorObject) => handleResponseError(error, false)
    )
  }

  const initialResponse = () => {
    if (props.fromEditMode === EditMode.ADD) {
      const flights = props.transactions.map((trans) => {
        return trans.flights[0] // first (only) flight in each add transaction
      })
      handleAddTransactions(flights)
    } else if (props.fromEditMode === EditMode.EDIT) {
      handleSendTransactions(false)
    }
  }

  const startResponseFlow = () => {
    dispatch({ type: "SET_WORKFLOW_STATE", payload: "INITIAL_SUBMIT" })
    initialResponse()
  }

  const startFinalResponseFlow = () => {
    setError(null)
    dispatch({ type: "SET_WORKFLOW_STATE", payload: "FINAL_SUBMIT" })
    setCurrentStep(onlyFinalResponse ? Step.InitialSubmit : Step.FinalSubmit)
    setIsLoading(true)
    handleSendTransactions(true)
  }

  const allowCommit = state.workFlowState === "INITIAL_RESPONSE"

  const allowClose = state.workFlowState === "FINAL_RESPONSE"

  // Handle dialog close or cancel
  // Should reject the transacation
  const handleClose = () => {
    setCurrentStep(Step.InitialSubmit)
    setResponseTransactions(null)
    setError(null)
    setInfoMsg(null)
    globalDispatch(endTransaction({}))

    if (allowClose) {
      store.dispatch(closeAlmostAllNotifications())
      props.onClose()
    } else {
      if (state.workFlowState !== "INITIAL_ERROR") {
        store.dispatch(closeAlmostAllNotifications())
      }
      props.onCancel()
    }
  }

  const handleUpdTransaction = (
    newTransaction: Transaction,
    transIdx: number
  ) => {
    const updTransactions = _.cloneDeep(responseTransactions)
    updTransactions[transIdx] = newTransaction
    setResponseTransactions(updTransactions)
  }

  const hasFlightWithTerminal = handleHasFlightsWithTerminal(
    props.airportSeasons,
    responseTransactions
  )

  const hasFlightWithParkingLoc = handleHasFlightsWithParkingLoc(
    props.airportSeasons,
    responseTransactions
  )

  const customShowLabelConfig = {
    terminal: {
      showLabelSection: hasFlightWithTerminal,
      showLabel: true
    },
    parkingLocation: {
      showLabelSection: hasFlightWithParkingLoc,
      showLabel: true
    },
    serviceTypeAcReg: {
      showLabel: true,
      showLabelSection: handleHasFlightsWithServiceTypeAcReg(
        props.airportSeasons,
        responseTransactions
      )
    }
  }

  const addFlightItems =
    responseTransactions &&
    responseTransactions.length > 0 &&
    responseTransactions.map((t, i) => (
      <TransactionItem
        key={i}
        transaction={t}
        editMode={EditMode.RESPONSE}
        airportSeasons={props.airportSeasons}
        pageGroup={transactionPageGroup}
        codeType={props.codeType}
        onChange={(newTransaction) => handleUpdTransaction(newTransaction, i)}
        isInitResponse={
          state.workFlowState === "INITIAL_RESPONSE" ||
          state.workFlowState === "INITIAL_ERROR"
        }
        customShowLabelConfig={customShowLabelConfig}
      />
    ))

  const getOverlayBodyContent = () => {
    const isFinal =
      state.workFlowState === "FINAL_RESPONSE" ||
      state.workFlowState === "FINAL_ERROR"
    const isInit =
      state.workFlowState === "INITIAL_RESPONSE" ||
      state.workFlowState === "INITIAL_ERROR"
    const isError =
      state.workFlowState === "FINAL_ERROR" ||
      state.workFlowState === "INITIAL_ERROR"
    return isError ? (
      <section className="d-flex flex-column align-items-center">
        <AlertBox
          type={error ? error.kind : NotificationKind.WARNING}
          message={
            error && error.message ? error.message : "Something went wrong..."
          }
        />
        <Button onClick={handleClose} className="ocs-mt-20">
          {"Return to " + _.startCase(props.fromEditMode) + " Page"}
        </Button>
      </section>
    ) : (
      <section className="d-flex flex-column align-items-end">
        {error && (
          <div className="ocs-notifications-container response-pane-error">
            <AlertBox
              type={error.kind}
              message={error.message}
              onClose={() => setError(null)}
            />
          </div>
        )}
        <div>
          <TransactionFlightHeaders
            editMode={EditMode.RESPONSE}
            flightMode={FlightMode.Turnaround}
            codeType={props.codeType}
            pageGroup={transactionPageGroup}
            isInitResponse={isInit}
            grcMode={GCRMode.FLT}
            sticky
            customShowLabelConfig={customShowLabelConfig}
          />
          {addFlightItems}
        </div>
        <div
          className={
            "d-flex w-100p ocs-mt-12 " +
            (infoMsg ? "justify-content-between" : "justify-content-end")
          }
        >
          {infoMsg && (
            <AlertBox type={NotificationKind.INFO} message={infoMsg} />
          )}
          {(!isFinal || siText) && (
            <div className="ocs-w-600">
              <TextArea
                name={FieldName.siText}
                value={siText}
                label="SI Text"
                onChange={(e) => {
                  setSiText(e.target.value)
                }}
                readonly={isFinal}
                rows={6}
                autoFocus
              />
            </div>
          )}
        </div>
      </section>
    )
  }

  const stepArray = onlyFinalResponse
    ? [ResponsePaneText.waitResponse, ResponsePaneText.response]
    : [
        ResponsePaneText.waitInitResponse,
        ResponsePaneText.initResponse,
        ResponsePaneText.waitFinalResponse,
        ResponsePaneText.finalResponse
      ]

  return (
    <div>
      {inTransaction && (
        <div className="ocs-response-pane-container">
          <PageTemplate
            pageTitle={PageTitle.responseFlow}
            pageClassName="ocs-response-pane-content"
            pageContentClassName="d-flex flex-column"
          >
            <div className="page-content-wrapper d-flex justify-content-between">
              <div className="ocs-w-128">
                <SeasonSelector
                  airportSeasons={props.airportSeasons}
                  supportsICAO={pageConfig.codeType === CodeType.ICAO}
                  seasonReadonly
                  timeReadonly
                />
              </div>
              <Stepper stepNameArr={stepArray} currentStep={currentStep} />
              <div className="d-flex justify-content-end align-items-center ocs-w-160">
                {!isLoading && (
                  <Fragment>
                    <Button
                      className={allowCommit ? "btn-margin" : ""}
                      onClick={handleClose}
                    >
                      {allowClose ? CommonText.close : CommonText.cancel}
                    </Button>
                    {allowCommit ? (
                      <Button
                        variant="primary"
                        onClick={startFinalResponseFlow}
                      >
                        {CommonText.commit}
                      </Button>
                    ) : null}
                  </Fragment>
                )}
              </div>
            </div>
            {isLoading ? (
              <div className="ocs-mt-52">
                <LoadingPanel
                  visible={true}
                  loadingTxt="Loading Response..."
                  overlay={false}
                />
              </div>
            ) : (
              <div
                className="ocs-mt-52 ocs-pb-20 transactions-container"
                style={{ overflowY: "auto" }}
              >
                {getOverlayBodyContent()}
              </div>
            )}
          </PageTemplate>
        </div>
      )}
    </div>
  )
}
